import type {
  AudienceFilterLabel,
  SelectedSegment,
} from "@digital-twins/shared-types";
import type { PopulationFilter } from "@/types/routes/test-results";

/**
 * The audience the study flow picked: the population, and the filter and saved
 * segments applied to it in the same pick (the audience filter modal).
 */
export interface SelectedPopulation {
  id: string;
  name: string;
  personaCount?: number;
  description?: string;
  audienceType?: string | null;
  contextText?: string | null;
  aiDescription?: string | null;
  populationFilter?: PopulationFilter | null;
  selectedSegments?: Array<SelectedSegment> | null;
  /** The filter in the filter catalog's labels, from the same pick (C09-4). */
  populationFilterLabels?: Array<AudienceFilterLabel> | null;
}

const PENDING_POPULATION_ID = "population_pending_selection";

/**
 * Whether a stored `draft.audience` names no population yet: a copy's, whose
 * audience is chosen in the wizard (B-c), or the retired placeholder. The
 * picked audience is written to such a draft before it is built or reviewed.
 */
export function draftNamesNoPopulation(audience: unknown): boolean {
  const populationId =
    audience !== null && typeof audience === "object"
      ? (audience as { populationId?: unknown }).populationId
      : undefined;
  return (
    typeof populationId !== "string" ||
    populationId.trim().length === 0 ||
    populationId === PENDING_POPULATION_ID
  );
}

/**
 * `draft.audience` for a picked audience: the one builder for create and for
 * every audience write (C09-2). The draft owns the population together with
 * its filter and segments, so a write replaces all three; a population picked
 * without them clears them. `estimatedRespondentCount` stays the population's
 * size (its stored meaning), never the filtered count. The filter's labels
 * (C09-4) travel only with the filter they describe; PLAN, revision and Review
 * read the filter from them.
 */
export function buildDraftAudience(
  selected: SelectedPopulation,
): Record<string, unknown> {
  return {
    populationId: selected.id,
    estimatedRespondentCount: selected.personaCount ?? 100,
    populationName: selected.name,
    populationDescription: selected.description ?? "",
    audienceType: selected.audienceType ?? "",
    contextText: selected.contextText ?? "",
    ...(selected.aiDescription
      ? { aiDescription: selected.aiDescription }
      : {}),
    ...(selected.populationFilter
      ? { populationFilter: selected.populationFilter }
      : {}),
    ...(selected.populationFilter && selected.populationFilterLabels?.length
      ? { populationFilterLabels: selected.populationFilterLabels }
      : {}),
    ...(selected.selectedSegments?.length
      ? { selectedSegments: selected.selectedSegments }
      : {}),
  };
}

function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(canonicalJson).join(",")}]`;
  }
  if (value !== null && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .filter((key) => record[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value ?? null);
}

/**
 * Which audience a stored or picked `draft.audience` names: the population,
 * its filter and its segments, independent of key order (the server stores
 * JSON that may come back reordered). Equal keys need no write. The filter's
 * labels count too, so a filter stored before it had labels gets them when it
 * is picked again.
 */
export function draftAudienceKey(audience: unknown): string {
  const stored =
    audience !== null && typeof audience === "object"
      ? (audience as Record<string, unknown>)
      : {};
  const segments = Array.isArray(stored.selectedSegments)
    ? stored.selectedSegments
    : [];
  const labels = Array.isArray(stored.populationFilterLabels)
    ? stored.populationFilterLabels
    : [];
  return canonicalJson({
    populationId: stored.populationId ?? null,
    populationFilter: stored.populationFilter ?? null,
    selectedSegments: segments.length > 0 ? segments : null,
    populationFilterLabels: labels.length > 0 ? labels : null,
  });
}
